import { useEffect, useState, useRef, useCallback } from "react"
import type {
  DiscoverFiltersProps,
  DiscoverFilters as DiscoverFiltersType,
  DiscoverSavedSearch,
  FilterSuggestion,
} from "~/react/types/discover"
import ProactiveAiPrompt from "~/react/components/proactive-ai-prompt"
import { Button } from "~/react/ui/button"
import { ToggleButton } from "~/react/ui/toggle-button"
import { Icon } from "~/react/ui/icon"
import { CompanyNameFilter } from "~/react/components/discover_filters/company_name_filter"
import { CompanySizeFilter } from "~/react/components/discover_filters/company_size_filter"
import { IndustryFilter } from "~/react/components/discover_filters/industry_filter"
import { CompanyTypeFilter } from "~/react/components/discover_filters/company_type_filter"
import { KeywordsFilter } from "~/react/components/discover_filters/keywords_filter"
import { FoundedYearFilter } from "~/react/components/discover_filters/founded_year_filter"
import { LocationFilter } from "~/react/components/discover_filters/location_filter"
import { SimilarCompaniesFilter } from "~/react/components/discover_filters/similar_companies_filter"
import { TechnologiesFilter } from "~/react/components/discover_filters/technologies_filter"
import { FundingsFilter } from "~/react/components/discover_filters/fundings_filter"
import { DiscoverCompactFilter } from "~/react/components/discover_filters/compact_filters_summary"
import { SavedSearchesButton } from "~/react/components/discover_filters/saved_searches_button"
import { SearchSaver } from "~/react/components/discover_filters/search_saver"
import SuggestionBox, {
  SuggestionBoxButtons,
  SuggestionBoxContent,
  SuggestionBoxLoadingText,
  SuggestionBoxUpdate,
} from "./suggestion_box"
import { TooltipProvider } from "~/react/ui/tooltip"

const t = (key: string) => window.i18n.t(key)

// Helper to ensure value is always an array (handles single string or object from saved searches)
// Flattens tuples to codes - labels are passed separately via locationLabels prop
function ensureArray(value: unknown): string[] {
  if (value === undefined || value === null) return []
  if (Array.isArray(value)) {
    return value.map((item) => {
      // Flatten tuples to just the code (first element)
      if (Array.isArray(item)) return String(item[0])
      return String(item)
    })
  }
  if (typeof value === "object") return Object.keys(value)
  return [String(value)]
}

interface DiscoverFiltersComponentProps extends DiscoverFiltersProps {
  // Filter state lifted from parent
  filters: DiscoverFiltersType
  updateFilter: <K extends keyof DiscoverFiltersType>(key: K, value: DiscoverFiltersType[K]) => void
  clearAllFilters: () => void
  hasFilters: boolean
  showInlinePrompt?: boolean
  onAiPromptSubmit?: (query: string) => void
  // Callback when filters change
  onFiltersChange: (filters: DiscoverFiltersType) => void
  // Saved search callbacks (no-reload)
  onLoadSavedSearch: (search: DiscoverSavedSearch) => void
  onSaveSearch: (name: string, existingViewId?: number) => Promise<boolean>
  // AI loading state and previous filters for diff calculation
  isAiLoading?: boolean
  previousFilters?: DiscoverFiltersType
  // Proactive suggestion props
  suggestion?: FilterSuggestion | null
  isSuggestionLoading?: boolean
  onApplySuggestion?: () => void
  onIgnoreSuggestion?: () => void
}

// Keys that are not real filters (operators are modifiers, not filters themselves)
const EXCLUDED_FILTER_KEYS = ["last_ai_query", "keywords_operator", "technology_operator"]

// Helper to calculate filter diff between previous and current filters
function calculateFilterDiff(
  previous: DiscoverFiltersType,
  current: DiscoverFiltersType,
): { added: number; removed: number; changed: number } {
  const isRealFilter = (k: string) => !EXCLUDED_FILTER_KEYS.includes(k)
  const prevKeys = Object.keys(previous).filter(isRealFilter)
  const currKeys = Object.keys(current).filter(isRealFilter)

  let added = 0
  let removed = 0
  let changed = 0

  // Count added filters (in current but not in previous)
  for (const key of currKeys) {
    if (!prevKeys.includes(key)) added++
  }

  // Count removed filters (in previous but not in current)
  for (const key of prevKeys) {
    if (!currKeys.includes(key)) removed++
  }

  // Count changed filters (in both but different values)
  for (const key of currKeys) {
    if (
      prevKeys.includes(key) &&
      JSON.stringify(previous[key as keyof DiscoverFiltersType]) !==
        JSON.stringify(current[key as keyof DiscoverFiltersType])
    ) {
      changed++
    }
  }

  return { added, removed, changed }
}

export default function DiscoverFilters({
  filterOptions,
  savedSearches = [],
  entitlement,
  viewId,
  filters,
  updateFilter,
  clearAllFilters,
  hasFilters,
  showInlinePrompt,
  onAiPromptSubmit,
  onFiltersChange,
  onLoadSavedSearch,
  onSaveSearch,
  isAiLoading = false,
  previousFilters = {},
  suggestion,
  isSuggestionLoading = false,
  onApplySuggestion,
  onIgnoreSuggestion,
}: DiscoverFiltersComponentProps) {
  const [searchName, setSearchName] = useState("")
  const [isCompactViewEnabled, setIsCompactViewEnabled] = useState(false)
  const [aiPromptPadding, setAiPromptPadding] = useState(0)
  const aiPromptRef = useRef<HTMLDivElement | null>(null)
  // State tracks the current suggestion box variant being displayed
  const [suggestionVariant, setSuggestionVariant] = useState<"loading" | "update" | "suggestion" | "hidden">("hidden")
  // Filter diff for the update variant
  const [filterDiff, setFilterDiff] = useState<{
    added: number
    removed: number
    changed: number
  } | null>(null)
  // Track if this is the initial mount to avoid re-applying existing filters
  const isInitialMount = useRef(true)
  // Track if we're doing a manual clear to skip auto-apply
  const isManualClear = useRef(false)
  // Track previous isAiLoading to detect when AI loading just finished
  const wasAiLoading = useRef(false)
  // Track unsaved changes for saved searches
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  // Track if we're loading a saved search to skip the first filter change
  const isLoadingSavedSearch = useRef(!!viewId)

  // Get the current view name if editing an existing saved search
  const currentView = viewId ? savedSearches.find((s) => s.id === viewId) : null

  // Initialize search name from current view when component mounts or viewId changes
  useEffect(() => {
    if (currentView) {
      setSearchName(currentView.name)
    }
  }, [viewId, currentView?.name])

  // Reset hasUnsavedChanges when viewId changes (loading a different saved search)
  useEffect(() => {
    setHasUnsavedChanges(false)
    isLoadingSavedSearch.current = true
  }, [viewId])

  // Track filter changes to set hasUnsavedChanges
  useEffect(() => {
    // Skip initial mount (filters are loaded from saved search)
    if (isLoadingSavedSearch.current) {
      isLoadingSavedSearch.current = false
      return
    }

    // Only track changes when viewing a saved search
    if (viewId) {
      setHasUnsavedChanges(true)
    }
  }, [filters, viewId])

  // Render suggestion box content based on current variant
  const renderSuggestionBoxContent = () => {
    switch (suggestionVariant) {
      case "loading":
        return <SuggestionBoxLoadingText>Translating prompt to filters</SuggestionBoxLoadingText>
      case "update":
        return (
          <SuggestionBoxUpdate
            added={filterDiff?.added ?? 0}
            removed={filterDiff?.removed ?? 0}
            changed={filterDiff?.changed ?? 0}
            onClose={() => setSuggestionVariant("hidden")}
          />
        )
      case "suggestion": {
        if (!suggestion) return null
        const hasFilters = suggestion.filters && Object.keys(suggestion.filters).length > 0
        return (
          <>
            <SuggestionBoxContent>
              <div className="h-suggestion-box__suggestion-text">
                <span className="h-suggestion-box__suggestion-rationale">{suggestion.quality_reasoning}</span>
                {hasFilters && suggestion.improvement !== 0 && (
                  <span className="h-suggestion-box__suggestion-count">
                    {suggestion.improvement > 0
                      ? ` +${suggestion.improvement.toLocaleString()} ${t("js.discover.suggestion_box.more_results")}`
                      : ` ${Math.abs(suggestion.improvement).toLocaleString()} ${t("js.discover.suggestion_box.less_results")}`}
                  </span>
                )}
              </div>
            </SuggestionBoxContent>
            {hasFilters && (
              <SuggestionBoxButtons>
                <Button variant="ghost" size="xs" onClick={onIgnoreSuggestion}>
                  {t("js.discover.suggestion_box.ignore")}
                </Button>
                <Button variant="default" size="xs" onClick={onApplySuggestion}>
                  {t("js.discover.suggestion_box.apply")}
                </Button>
              </SuggestionBoxButtons>
            )}
          </>
        )
      }
      case "hidden":
        return null
      default:
        return null
    }
  }

  // Manage suggestion box state based on AI loading
  useEffect(() => {
    if (!showInlinePrompt) {
      setSuggestionVariant("hidden")
      wasAiLoading.current = isAiLoading
      return
    }

    if (isAiLoading) {
      setSuggestionVariant("loading")
      wasAiLoading.current = true
      return
    }

    // Only show update when AI loading just finished (transition from loading to not loading)
    // Don't recalculate on manual filter changes
    const justFinishedLoading = wasAiLoading.current && !isAiLoading
    wasAiLoading.current = false

    if (!justFinishedLoading) {
      return
    }

    // Only show update for refinements (when there were previous filters)
    // For initial searches, hide immediately after loading
    if (previousFilters && Object.keys(previousFilters).length > 0) {
      const diff = calculateFilterDiff(previousFilters, filters)
      if (diff.added > 0 || diff.removed > 0 || diff.changed > 0) {
        setFilterDiff(diff)
        setSuggestionVariant("update")
      } else {
        setSuggestionVariant("hidden")
      }
    } else {
      // Initial search - no update message, just hide
      setSuggestionVariant("hidden")
    }
  }, [isAiLoading, showInlinePrompt, filters, previousFilters])

  // Show proactive suggestion when received (and not showing other variants)
  useEffect(() => {
    if (!showInlinePrompt) return

    if (suggestion && suggestionVariant === "hidden") {
      setSuggestionVariant("suggestion")
    } else if (!suggestion && suggestionVariant === "suggestion") {
      setSuggestionVariant("hidden")
    }
  }, [suggestion, suggestionVariant, showInlinePrompt])

  // Reserve vertical space equal to the inline AI prompt height and suggestion box height.
  // Recalculate whenever the prompt mounts/unmounts, its size changes, or variant changes.
  useEffect(() => {
    if (!showInlinePrompt || !aiPromptRef.current) {
      setAiPromptPadding(0)
      return
    }

    const updatePadding = () => {
      if (!aiPromptRef.current) return
      // Use the stable wrapper div for measurement - this div never unmounts,
      // so ResizeObserver can reliably track it across variant changes
      const wrapperNode = aiPromptRef.current.querySelector<HTMLElement>(".collapsable-filters__ai-prompt-wrapper")

      if (wrapperNode) {
        setAiPromptPadding(wrapperNode.offsetHeight + 24)
      }
    }

    requestAnimationFrame(() => {
      updatePadding()
    })

    const resizeObserver = typeof ResizeObserver !== "undefined" ? new ResizeObserver(() => updatePadding()) : null

    if (resizeObserver && aiPromptRef.current) {
      const wrapperNode = aiPromptRef.current.querySelector<HTMLElement>(".collapsable-filters__ai-prompt-wrapper")
      if (wrapperNode) resizeObserver.observe(wrapperNode)
    }

    window.addEventListener("resize", updatePadding)

    return () => {
      window.removeEventListener("resize", updatePadding)
      if (resizeObserver) resizeObserver.disconnect()
    }
  }, [showInlinePrompt, suggestionVariant])

  // Defensive fallback - filterOptions should always be provided by prepare_react_props
  // but this prevents runtime errors if something goes wrong during prop passing
  if (!filterOptions) {
    return (
      <div className="p-4 text-center">
        <p className="text-sm text-muted">Loading filters...</p>
      </div>
    )
  }

  // Handle Clear All: clear local state and notify parent
  const handleClearAll = useCallback(() => {
    // Set flag to skip auto-apply effect
    isManualClear.current = true
    clearAllFilters()
    onFiltersChange({})
  }, [clearAllFilters, onFiltersChange])

  const handleApplyFilters = useCallback(() => {
    onFiltersChange(filters)
  }, [filters, onFiltersChange])

  // Auto-apply filters when they change (but not on initial mount or manual clear)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }

    // Skip auto-apply if we just did a manual clear (already navigated)
    if (isManualClear.current) {
      isManualClear.current = false
      return
    }

    const timer = setTimeout(() => {
      handleApplyFilters()
    }, 500) // Debounce 500ms

    return () => clearTimeout(timer)
  }, [handleApplyFilters])

  return (
    <div className="collapsable-filters">
      <div className="collapsable-filters__header">
        <h2 className="collapsable-filters__title">{t("js.back.discover.title")}</h2>

        <div className="collapsable-filters__actions">
          {/* Clear All - shows when filters exist */}
          {hasFilters && (
            <>
              <ToggleButton
                type="button"
                variant="ghost"
                size="sm"
                iconOnly
                selected={isCompactViewEnabled}
                onSelectedChange={setIsCompactViewEnabled}
                aria-label={t("js.back.discover.compact.toggle_view")}
              >
                <Icon icon="arrows-to-dotted-line" aria-hidden="true" />
              </ToggleButton>
              <Button variant="default" size="sm" onClick={handleClearAll}>
                {t("js.back.discover.clear_all")}
              </Button>
            </>
          )}
        </div>
      </div>

      {showInlinePrompt && (
        <div ref={aiPromptRef} className="collapsable-filters__ai-prompt">
          <div className="collapsable-filters__ai-prompt-wrapper">
            {suggestionVariant !== "hidden" && (
              <TooltipProvider>
                <SuggestionBox variant={suggestionVariant}>{renderSuggestionBoxContent()}</SuggestionBox>
              </TooltipProvider>
            )}
            <ProactiveAiPrompt isSmall onSubmit={onAiPromptSubmit} />
          </div>
        </div>
      )}

      {isCompactViewEnabled ? (
        <div className="collapsable-filters__compact">
          <DiscoverCompactFilter
            filters={filters}
            filterOptions={filterOptions}
            onDisableCompactView={() => setIsCompactViewEnabled(false)}
          />
        </div>
      ) : (
        <div
          className="collapsable-filters__default"
          style={aiPromptPadding > 0 ? { paddingBottom: `${aiPromptPadding}px` } : undefined}
        >
          <LocationFilter
            includedContinentValues={ensureArray(filters.location_continent_included)}
            excludedContinentValues={ensureArray(filters.location_continent_excluded)}
            includedBusinessRegionValues={ensureArray(filters.location_business_region_included)}
            excludedBusinessRegionValues={ensureArray(filters.location_business_region_excluded)}
            includedCountryValues={ensureArray(filters.location_country_included)}
            excludedCountryValues={ensureArray(filters.location_country_excluded)}
            includedStateValues={ensureArray(filters.location_state_included)}
            excludedStateValues={ensureArray(filters.location_state_excluded)}
            includedCityValues={ensureArray(filters.location_city_included)}
            excludedCityValues={ensureArray(filters.location_city_excluded)}
            onChangeIncludedContinent={(value) => updateFilter("location_continent_included", value)}
            onChangeExcludedContinent={(value) => updateFilter("location_continent_excluded", value)}
            onChangeIncludedBusinessRegion={(value) => updateFilter("location_business_region_included", value)}
            onChangeExcludedBusinessRegion={(value) => updateFilter("location_business_region_excluded", value)}
            onChangeIncludedCountry={(value) => updateFilter("location_country_included", value)}
            onChangeExcludedCountry={(value) => updateFilter("location_country_excluded", value)}
            onChangeIncludedState={(value) => updateFilter("location_state_included", value)}
            onChangeExcludedState={(value) => updateFilter("location_state_excluded", value)}
            onChangeIncludedCity={(value) => updateFilter("location_city_included", value)}
            onChangeExcludedCity={(value) => updateFilter("location_city_excluded", value)}
            countryNames={filterOptions.country_names}
            stateNames={filterOptions.state_names}
          />

          <IndustryFilter
            includedValues={ensureArray(filters.industry_included)}
            excludedValues={ensureArray(filters.industry_excluded)}
            industries={filterOptions.industries}
            onChangeIncluded={(value) => updateFilter("industry_included", value)}
            onChangeExcluded={(value) => updateFilter("industry_excluded", value)}
          />

          <KeywordsFilter
            includedValues={ensureArray(filters.keywords_included)}
            excludedValues={ensureArray(filters.keywords_excluded)}
            operator={(filters.keywords_operator as "and" | "or") || "and"}
            onChangeIncluded={(value) => updateFilter("keywords_included", value)}
            onChangeExcluded={(value) => updateFilter("keywords_excluded", value)}
            onChangeOperator={(value) => updateFilter("keywords_operator", value)}
          />

          <CompanySizeFilter
            value={ensureArray(filters.company_size)}
            sizes={filterOptions.company_sizes}
            onChange={(value) => updateFilter("company_size", value)}
          />

          <CompanyNameFilter
            value={ensureArray(filters.domains)}
            onChange={(value) => updateFilter("domains", value)}
          />

          <SimilarCompaniesFilter
            value={filters.similar_to_domain}
            onChange={(value) => updateFilter("similar_to_domain", value)}
            isLocked={!entitlement.allowSimilarCompaniesFilter}
          />

          <CompanyTypeFilter
            includedValues={ensureArray(filters.company_type_included)}
            excludedValues={ensureArray(filters.company_type_excluded)}
            companyTypes={filterOptions.company_types}
            onChangeIncluded={(value) => updateFilter("company_type_included", value)}
            onChangeExcluded={(value) => updateFilter("company_type_excluded", value)}
          />

          <FoundedYearFilter
            fromValue={filters.founded_year_from}
            toValue={filters.founded_year_to}
            includedValues={ensureArray(filters.founded_year_included)}
            excludedValues={ensureArray(filters.founded_year_excluded)}
            onChangeFrom={(value) => updateFilter("founded_year_from", value)}
            onChangeTo={(value) => updateFilter("founded_year_to", value)}
            onChangeIncluded={(value) => updateFilter("founded_year_included", value)}
            onChangeExcluded={(value) => updateFilter("founded_year_excluded", value)}
            isLocked={!entitlement.allowFoundedYearFilter}
          />

          <TechnologiesFilter
            includedValues={ensureArray(filters.technology_included)}
            excludedValues={ensureArray(filters.technology_excluded)}
            operator={(filters.technology_operator as "and" | "or") || "and"}
            technologies={filterOptions.technologies || {}}
            onChangeIncluded={(value) => updateFilter("technology_included", value)}
            onChangeExcluded={(value) => updateFilter("technology_excluded", value)}
            onChangeOperator={(value) => updateFilter("technology_operator", value)}
            isLocked={!entitlement.allowTechnologiesFilter}
          />

          <FundingsFilter
            seriesValues={ensureArray(filters.funding_series)}
            amountFrom={filters.funding_amount_from}
            amountTo={filters.funding_amount_to}
            dateValue={filters.funding_date}
            dateFrom={filters.funding_date_from}
            dateTo={filters.funding_date_to}
            onChangeSeriesValues={(value) => updateFilter("funding_series", value)}
            onChangeAmountFrom={(value) => updateFilter("funding_amount_from", value)}
            onChangeAmountTo={(value) => updateFilter("funding_amount_to", value)}
            onChangeDateValue={(value) => updateFilter("funding_date", value)}
            onChangeDateFrom={(value) => updateFilter("funding_date_from", value)}
            onChangeDateTo={(value) => updateFilter("funding_date_to", value)}
            isLocked={!entitlement.allowFundingsFilter}
          />
        </div>
      )}

      <div className="collapsable-filters__footer">
        {hasFilters && (
          <SearchSaver
            viewId={viewId}
            searchName={searchName}
            hasUnsavedChanges={hasUnsavedChanges}
            onSave={onSaveSearch}
            setHasUnsavedChanges={setHasUnsavedChanges}
          />
        )}
        <SavedSearchesButton savedSearches={savedSearches} hasFilters={hasFilters} onLoadSearch={onLoadSavedSearch} />
      </div>
    </div>
  )
}
